import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Check, ChevronUp, CircleCheck, Clock, Download, Inbox, Sparkles, TriangleAlert, Volume2, VolumeX } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConnectionDot } from '@/components/layout/AppHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { ClassPulse, type ConfusionSummary } from '@/components/ClassPulse';
import { useWakeLock } from '@/hooks/useWakeLock';
import { playChime, primeChime } from '@/lib/chime';
import { getProfessorToken } from '../authCookie';

interface Question {
  question_id: string;
  question_text: string;
  votes: number;
  timestamp: string;
  is_answered: boolean;
  answered_at?: string;
}

function formatResponseTime(submittedAt: string, answeredAt: string): string {
  const submitted = new Date(submittedAt).getTime();
  const answered = new Date(answeredAt).getTime();
  const diffMs = answered - submitted;

  if (isNaN(diffMs) || diffMs < 0) return '';

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${seconds}s`;
  }
}

function formatTimestamp(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString();
  } catch {
    return isoString;
  }
}

function formatSessionDate(isoOrTimestamp: string): string {
  try {
    const d = new Date(isoOrTimestamp);
    if (Number.isNaN(d.getTime())) return isoOrTimestamp;
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoOrTimestamp;
  }
}

interface InsightResponse {
  bullets: string[];
  used_llm: boolean;
  provider: string;
  model: string | null;
}

const STOP_WORDS = new Set([
  'what', 'is', 'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'on', 'for',
  'how', 'does', 'do', 'can', 'you', 'explain', 'please', 'again', 'could',
  'difference', 'between', 'about', 'with', 'work', 'works', 'we', 'i', 'go',
  'over', 'me', 'my', 'it', 'this', 'that', 'are', 'be', 'have', 'has', 'was',
  'were', 'been', 'being', 'there', 'their', 'they', 'them', 'some', 'any',
  'more', 'most', 'other', 'into', 'through', 'during', 'before', 'after',
  'above', 'below', 'up', 'down', 'out', 'off', 'why', 'when', 'where', 'which',
  'who', 'whom', 'would', 'should', 'could', 'might', 'must', 'shall', 'will',
]);

function extractKeywords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w && w.length > 2 && !STOP_WORDS.has(w));
}

function getTopicLabel(questionText: string): string {
  const keywords = extractKeywords(questionText);
  if (keywords.length === 0) return 'general concepts';
  if (keywords.length === 1) return keywords[0];
  if (keywords.length === 2) return `${keywords[0]} and ${keywords[1]}`;
  return `${keywords[0]}, ${keywords[1]}, and related topics`;
}

function generateLocalInsights(questions: Question[]): string[] {
  if (questions.length === 0) return [];

  const bulletCount = questions.length === 1 ? 1 : questions.length === 2 ? 2 : 3;
  const topQuestions = questions.slice(0, bulletCount);
  const templates = [
    'Students need clarification on {topic}.',
    "There's interest in understanding {topic} better.",
    'Consider addressing {topic} - students are asking about this.',
  ];

  return topQuestions.map((q, idx) => {
    const topic = getTopicLabel(q.question_text);
    return templates[idx % templates.length].replace('{topic}', topic);
  });
}

interface PreviousQuestion {
  question_id: string;
  question_text: string;
  votes: number;
  question_timestamp: string;
  session_id: string;
  session_timecreated: string;
}

interface ProfessorInterfaceProps {
  sessionId: string;
  classCode: string;
  className: string;
  backendUrl?: string;
  professorName?: string;
  sessionTimecreated?: string;
  previousQuestions?: PreviousQuestion[];
  onEndSession?: () => void;
  onStartNewSession?: () => Promise<void>;
  onBackToHome?: () => void;
}

/**
 * ✅ Presence v2 (best practice):
 * - Identify professor once per WS connection: {type:'hello', role:'professor'}
 * - NO active_signal heartbeats (backend ends session if 0 prof connections for 5 minutes)
 */
type WsOutgoing =
  // Carries the professor session token: the backend verifies it owns this
  // class before counting the socket as professor presence.
  | { type: 'hello'; role: 'professor'; token: string }
  | { type: 'ping' };

const ProfessorInterface: React.FC<ProfessorInterfaceProps> = ({
  sessionId,
  classCode,
  className,
  backendUrl = 'http://localhost:8000',
  professorName,
  sessionTimecreated,
  previousQuestions = [],
  onEndSession,
  onStartNewSession,
  onBackToHome,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [outstandingQuestions, setOutstandingQuestions] = useState<Question[]>([]);
  const [answeredQuestionsLog, setAnsweredQuestionsLog] = useState<Question[]>([]);
  const [sessionSummary, setSessionSummary] = useState<{
    total: number;
    answered: number;
    outstanding: number;
  } | null>(null);

  const [sessionInsights, setSessionInsights] = useState<string[]>([]);
  const [confusion, setConfusion] = useState<ConfusionSummary | null>(null);

  const SOUND_KEY = 'lectureme:new-question-sound';
  const [soundOn, setSoundOn] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SOUND_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const soundOnRef = useRef(soundOn);
  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  // Keep the phone awake for as long as the class is actually running.
  useWakeLock(!sessionEnded);

  // An AudioContext starts suspended until the page has seen a gesture; take
  // the first one that comes along so the first question is not silent.
  useEffect(() => {
    const prime = () => primeChime();
    window.addEventListener('pointerdown', prime, { once: true });
    window.addEventListener('keydown', prime, { once: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  // Mirrors the ids already on screen so a websocket resend after a reconnect
  // does not chime for a question the professor has seen.
  const knownQuestionIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    knownQuestionIdsRef.current = new Set(questions.map((q) => q.question_id));
  }, [questions]);

  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
      } catch {
        // A blocked localStorage should not stop the toggle working this session.
      }
      if (next) {
        primeChime();
        playChime();
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfusion(null);
    fetch(`${backendUrl}/api/confusion/${sessionId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.summary) setConfusion(data.summary as ConfusionSummary);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sessionId, backendUrl]);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const isUnmountingRef = useRef<boolean>(false);
  const sessionEndedRef = useRef(false);

  const RECONNECT_BASE_MS = 1000;
  const RECONNECT_MAX_MS = 8000;
  const reconnectAttemptRef = useRef<number>(0);

  useEffect(() => {
    sessionEndedRef.current = sessionEnded;
  }, [sessionEnded]);

  useEffect(() => {
    setSessionEnded(false);
    sessionEndedRef.current = false;
    setOutstandingQuestions([]);
    setAnsweredQuestionsLog([]);
    setSessionSummary(null);
    setQuestions([]);
    setError(null);
  }, [sessionId]);

  const fetchQuestions = useCallback(async (): Promise<Question[] | null> => {
    try {
      const token = getProfessorToken();
      const response = await fetch(`${backendUrl}/api/questions/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      const qs: Question[] = data.questions || [];
      setQuestions(qs);
      setError(null);
      return qs;
    } catch (err) {
      setError('Failed to fetch questions');
      console.error('Error fetching questions:', err);
      return null;
    }
  }, [sessionId, backendUrl]);

  const safeWsSend = useCallback((msg: WsOutgoing) => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch {}
  }, []);

  const connectWsRef = useRef<() => void>(() => {});
  const authFailedRef = useRef(false);

  const scheduleReconnect = useCallback(() => {
    if (isUnmountingRef.current) return;
    // See StudentInterface: a rejected token will not start passing on retry.
    if (authFailedRef.current) return;
    if (reconnectTimerRef.current != null) return;

    reconnectAttemptRef.current += 1;
    const backoff = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, reconnectAttemptRef.current - 1)
    );
    const jitter = Math.floor(Math.random() * 300);
    const delay = backoff + jitter;

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      connectWsRef.current();
    }, delay);
  }, []);

  const closeWs = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
  }, []);

  const handleRemoteSessionEnded = useCallback(
    async (reason?: string) => {
      // When backend broadcasts session_ended (auto or manual), we build summary from DB
      setSessionEnded(true);

      const qs = await fetchQuestions();
      const all = qs || [];
      const answered = all.filter(q => q.is_answered);
      const outstanding = all.filter(q => !q.is_answered);

      setOutstandingQuestions(outstanding);
      setAnsweredQuestionsLog(answered);
      setSessionSummary({
        total: all.length,
        answered: answered.length,
        outstanding: outstanding.length,
      });

      // Stop trying to reconnect once ended
      closeWs();
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (reason) {
        console.log('Session ended by backend:', reason);
      }
    },
    [fetchQuestions, closeWs]
  );

  const connectWs = useCallback(() => {
    if (isUnmountingRef.current) return;

    // Close any previous socket
    closeWs();

    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${sessionId}`;

    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      setError(null);

      // REQUIRED for backend presence logic, and now also authenticates the
      // socket — without a valid owning-professor token the server closes it.
      const token = getProfessorToken();
      if (!token) {
        authFailedRef.current = true;
        setError('Your session expired. Please log in again.');
        return;
      }
      safeWsSend({ type: 'hello', role: 'professor', token });
    };

    websocket.onmessage = event => {
      let message: any;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message.type === 'new_question') {
        const q: Question = message.question;
        if (!knownQuestionIdsRef.current.has(q.question_id)) {
          knownQuestionIdsRef.current.add(q.question_id);
          if (soundOnRef.current && !sessionEndedRef.current) playChime();
        }
        // de-dupe if server resend / reconnect fetch overlap
        setQuestions(prev => {
          if (prev.some(x => x.question_id === q.question_id)) return prev;
          return [...prev, q];
        });
      } else if (message.type === 'question_answered') {
        setQuestions(prev =>
          prev.map(q =>
            q.question_id === message.question_id
              ? { ...q, is_answered: true, answered_at: message.answered_at }
              : q
          )
        );
      } else if (message.type === 'question_upvoted' || message.type === 'question_downvoted') {
        setQuestions(prev =>
          prev.map(q =>
            q.question_id === message.question_id ? { ...q, votes: message.votes } : q
          )
        );
      } else if (message.type === 'confusion_updated') {
        if (message.summary) setConfusion(message.summary as ConfusionSummary);
      } else if (message.type === 'session_ended') {
        // new backend may include reason; counts might be absent
        void handleRemoteSessionEnded(message.reason);
      } else if (message.type === 'error') {
        if (message.code === 'auth_required') {
          authFailedRef.current = true;
          setError('Your session expired. Please log in again.');
          return;
        }
        setError(message.message || 'Server error');
      }
    };

    websocket.onerror = () => {
      setIsConnected(false);
      setError('WebSocket connection error');
    };

    websocket.onclose = () => {
      setIsConnected(false);
      if (!isUnmountingRef.current && !sessionEndedRef.current) {
        scheduleReconnect();
      }
    };
  }, [backendUrl, sessionId, safeWsSend, closeWs, scheduleReconnect, handleRemoteSessionEnded]);

  connectWsRef.current = connectWs;

  useEffect(() => {
    void fetchQuestions();

    isUnmountingRef.current = false;

    // Defer so React 18 StrictMode cleanup can cancel before a socket is created
    const connectTimer = window.setTimeout(() => {
      connectWsRef.current();
    }, 0);

    return () => {
      isUnmountingRef.current = true;
      window.clearTimeout(connectTimer);

      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      closeWs();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQuestions, closeWs]);

  const markAsAnswered = async (questionId: string) => {
    const token = getProfessorToken();

    // optimistic: mark answered immediately
    setQuestions(prev =>
      prev.map(q => (q.question_id === questionId ? { ...q, is_answered: true } : q))
    );

    try {
      const response = await fetch(`${backendUrl}/api/questions/mark-answered`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, question_id: questionId }),
      });

      if (!response.ok) throw new Error('Failed to mark question as answered');

      const data = await response.json();
      const answeredAt = data.answered_at;

      // reconcile: fill answered_at (and ensure answered=true)
      setQuestions(prev =>
        prev.map(q =>
          q.question_id === questionId ? { ...q, is_answered: true, answered_at: answeredAt } : q
        )
      );
    } catch (err) {
      console.error('Error marking question as answered:', err);
      setError('Failed to mark question as answered');

      // revert optimistic update
      setQuestions(prev =>
        prev.map(q => (q.question_id === questionId ? { ...q, is_answered: false } : q))
      );
    }
  };

  const endSession = async () => {
    try {
      const token = getProfessorToken();
      const response = await fetch(`${backendUrl}/api/session/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!response.ok) throw new Error('Failed to end session');

      const data = await response.json();
      setSessionEnded(true);
      setOutstandingQuestions(data.outstanding_questions);
      setAnsweredQuestionsLog(data.answered_questions_list || []);
      setSessionSummary({
        total: data.total_questions,
        answered: data.answered_questions,
        outstanding: data.outstanding_questions.length,
      });
      setQuestions([]);

      // stop WS + reconnect
      closeWs();
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (onEndSession) onEndSession();
    } catch (err) {
      console.error('Error ending session:', err);
      setError('Failed to end session');
    }
  };

  const downloadQuestionLog = () => {
    if (!sessionSummary) return;

    const timestamp = new Date().toISOString();
    const date = new Date().toLocaleString();

    let logContent = `Question Log - ${classCode} (${className})\n`;
    logContent += `Session ID: ${sessionId}\n`;
    logContent += `Session End Date: ${date}\n`;
    logContent += `${'='.repeat(80)}\n\n`;
    logContent += `SESSION SUMMARY\n`;
    logContent += `${'-'.repeat(80)}\n`;
    logContent += `Total Questions Received: ${sessionSummary.total}\n`;
    logContent += `Questions Answered: ${sessionSummary.answered}\n`;
    logContent += `Outstanding Questions: ${sessionSummary.outstanding}\n\n`;

    if (answeredQuestionsLog.length > 0) {
      logContent += `${'='.repeat(80)}\n`;
      logContent += `ANSWERED QUESTIONS\n`;
      logContent += `${'='.repeat(80)}\n\n`;

      answeredQuestionsLog
        .sort((a, b) => {
          if (a.answered_at && b.answered_at) {
            return new Date(a.answered_at).getTime() - new Date(b.answered_at).getTime();
          }
          return a.timestamp.localeCompare(b.timestamp);
        })
        .forEach((q, index) => {
          logContent += `${index + 1}. Question ID: ${q.question_id}\n`;
          logContent += `   Votes: ${q.votes}\n`;
          logContent += `   Question: ${q.question_text}\n`;
          logContent += `   Submitted: ${q.timestamp}\n`;
          if (q.answered_at) {
            logContent += `   Answered At: ${q.answered_at}\n`;
            logContent += `   Response Time: ${formatResponseTime(q.timestamp, q.answered_at)}\n`;
          }
          logContent += `\n`;
        });
    }

    if (outstandingQuestions.length > 0) {
      logContent += `${'='.repeat(80)}\n`;
      logContent += `OUTSTANDING QUESTIONS (Not Addressed)\n`;
      logContent += `${'='.repeat(80)}\n\n`;

      outstandingQuestions
        .sort((a, b) => {
          if (b.votes !== a.votes) return b.votes - a.votes;
          return a.timestamp.localeCompare(b.timestamp);
        })
        .forEach((q, index) => {
          logContent += `${index + 1}. Question ID: ${q.question_id}\n`;
          logContent += `   Votes: ${q.votes}\n`;
          logContent += `   Question: ${q.question_text}\n`;
          logContent += `   Submitted: ${q.timestamp}\n`;
          logContent += `\n`;
        });
    } else if (answeredQuestionsLog.length > 0) {
      logContent += `${'='.repeat(80)}\n`;
      logContent += `All questions were addressed during this session!\n`;
      logContent += `${'='.repeat(80)}\n`;
    }

    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `question-log-${sessionId}-${timestamp.split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const unansweredQuestions = useMemo(
    () =>
      questions
        .filter(q => !q.is_answered)
        .sort((a, b) => {
          if (b.votes !== a.votes) return b.votes - a.votes;
          return a.timestamp.localeCompare(b.timestamp);
        }),
    [questions]
  );

  const answeredQuestions = useMemo(() => questions.filter(q => q.is_answered), [questions]);

  const fetchInsights = useCallback(
    async (questionTexts: string[]): Promise<InsightResponse | null> => {
      if (questionTexts.length === 0) return null;

      try {
        const insightToken = getProfessorToken();
        const response = await fetch(`${backendUrl}/api/insights`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(insightToken ? { Authorization: `Bearer ${insightToken}` } : {}),
          },
          body: JSON.stringify({ questions: questionTexts, max_bullets: 3 }),
        });

        if (!response.ok) throw new Error(`API error: ${response.status}`);
        return await response.json();
      } catch (err) {
        console.error('Failed to fetch insights from API:', err);
        return null;
      }
    },
    [backendUrl]
  );

  useEffect(() => {
    if (!sessionEnded || outstandingQuestions.length === 0) {
      setSessionInsights(prev => (prev.length === 0 ? prev : []));
      return;
    }

    const sortedOutstanding = [...outstandingQuestions].sort((a, b) => {
      if (b.votes !== a.votes) return b.votes - a.votes;
      return a.timestamp.localeCompare(b.timestamp);
    });

    const questionTexts = sortedOutstanding.map(q => q.question_text);

    fetchInsights(questionTexts).then(result => {
      if (result) setSessionInsights(result.bullets);
      else setSessionInsights(generateLocalInsights(sortedOutstanding));
    });
  }, [sessionEnded, outstandingQuestions, fetchInsights]);

  if (sessionEnded && sessionSummary) {
    const stats = [
      { label: 'Questions asked', value: sessionSummary.total },
      { label: 'Answered', value: sessionSummary.answered },
      { label: 'Still open', value: sessionSummary.outstanding },
    ];
    const sortedOutstanding = [...outstandingQuestions].sort((a, b) => {
      if (b.votes !== a.votes) return b.votes - a.votes;
      return a.timestamp.localeCompare(b.timestamp);
    });

    return (
      <main className="container max-w-3xl py-10">
        <p className="text-sm font-medium text-muted-foreground">
          {classCode} · {className}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Class ended</h1>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-xl border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{stat.label}</p>
              <p className="mt-2 text-3xl font-bold tracking-tight">{stat.value}</p>
            </div>
          ))}
        </div>

        {sessionInsights.length > 0 && (
          <section className="mt-8 rounded-xl border bg-card p-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand-text" aria-hidden />
              <h2 className="text-base font-semibold">Where students still needed help</h2>
            </div>
            <ul className="mt-4 space-y-2.5 text-sm leading-relaxed">
              {sessionInsights.map((text, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-8">
          {sortedOutstanding.length > 0 ? (
            <>
              <h2 className="text-lg font-semibold tracking-tight">Not addressed</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                These questions were still open when class ended.
              </p>
              <ul className="mt-4 divide-y rounded-xl border bg-card">
                {sortedOutstanding.map((question) => (
                  <li key={question.question_id} className="flex items-start gap-4 p-4">
                    <div className="flex w-10 shrink-0 flex-col items-center pt-0.5 text-muted-foreground">
                      <ChevronUp className="h-4 w-4" aria-hidden />
                      <span className="text-sm font-semibold text-foreground">{question.votes}</span>
                    </div>
                    <p className="flex-1 text-[15px] leading-relaxed">{question.question_text}</p>
                    <Badge variant="warning" className="shrink-0">
                      <TriangleAlert className="h-3 w-3" aria-hidden />
                      Open
                    </Badge>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Alert variant="success">
              <CircleCheck />
              <AlertDescription className="font-medium">Every question was addressed during this class.</AlertDescription>
            </Alert>
          )}
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <Button
            size="lg"
            onClick={() => {
              if (onStartNewSession) void onStartNewSession();
            }}
            disabled={!onStartNewSession}
          >
            Start another class
          </Button>
          <Button variant="outline" size="lg" onClick={downloadQuestionLog}>
            <Download aria-hidden />
            Download log
          </Button>
          {onBackToHome && (
            <Button variant="ghost" size="lg" onClick={onBackToHome}>
              Back to courses
            </Button>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="container max-w-3xl py-10">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{className}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-2.5 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{classCode}</span>
            {sessionTimecreated && <span>Began {formatSessionDate(sessionTimecreated)}</span>}
            <ConnectionDot connected={isConnected} />
            <button
              type="button"
              onClick={toggleSound}
              aria-pressed={soundOn}
              title={
                soundOn
                  ? 'A short chime plays when a new question arrives'
                  : 'New questions arrive silently'
              }
              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {soundOn ? (
                <Volume2 className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <VolumeX className="h-3.5 w-3.5" aria-hidden />
              )}
              {soundOn ? 'Sound on' : 'Sound off'}
            </button>
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="shrink-0 text-destructive-soft-foreground hover:bg-destructive-soft">
              End class
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>End this class?</AlertDialogTitle>
              <AlertDialogDescription>
                Students will no longer be able to submit or vote. You'll get a summary and can download the
                question log.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep going</AlertDialogCancel>
              <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={endSession}>
                End class
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <ClassPulse summary={confusion} className="mt-8" />

      <Tabs defaultValue="new" className="mt-8">
        <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0">
          <TabsTrigger
            value="new"
            className="gap-2 rounded-none border-b-2 border-transparent px-0 pb-3 pt-0 text-sm font-semibold text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
          >
            New
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
              {unansweredQuestions.length}
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="answered"
            className="gap-2 rounded-none border-b-2 border-transparent px-0 pb-3 pt-0 text-sm font-semibold text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
          >
            Answered
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
              {answeredQuestions.length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-6">
          {unansweredQuestions.length === 0 ? (
            <div className="rounded-xl border border-dashed">
              <EmptyState
                icon={<Inbox />}
                title="No open questions"
                description="New questions from students will show up here, most upvoted first."
              />
            </div>
          ) : (
            <ul className="divide-y rounded-xl border bg-card">
              {unansweredQuestions.map((question) => (
                <li
                  key={question.question_id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="flex items-start gap-4 sm:contents">
                    <div className="flex w-10 shrink-0 flex-col items-center text-muted-foreground">
                      <ChevronUp className="h-4 w-4" aria-hidden />
                      <span className="text-sm font-semibold tabular-nums text-foreground">{question.votes}</span>
                    </div>
                    <p className="flex-1 text-[15px] leading-relaxed">{question.question_text}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 self-end sm:self-auto"
                    onClick={() => markAsAnswered(question.question_id)}
                  >
                    <Check aria-hidden />
                    Mark answered
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="answered" className="mt-6">
          {answeredQuestions.length === 0 ? (
            <div className="rounded-xl border border-dashed">
              <EmptyState
                icon={<CircleCheck />}
                title="Nothing answered yet"
                description="Questions you mark as answered move here with their response time."
              />
            </div>
          ) : (
            <ul className="divide-y rounded-xl border bg-card">
              {answeredQuestions.map((question) => (
                <li key={question.question_id} className="flex items-start gap-4 p-4">
                  <div className="flex w-10 shrink-0 flex-col items-center pt-0.5">
                    <CircleCheck className="h-4 w-4 text-success" aria-hidden />
                    <span className="text-sm font-semibold tabular-nums text-muted-foreground">{question.votes}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] leading-relaxed text-muted-foreground">{question.question_text}</p>
                    {question.answered_at && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden />
                        Answered {formatTimestamp(question.answered_at)}
                        {question.timestamp && (
                          <span>· {formatResponseTime(question.timestamp, question.answered_at)} after it was asked</span>
                        )}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default ProfessorInterface;
